import {
  Box,
  Card,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import Iconify from '@src/components/iconify';
import { ReferralStatusChip, RewardStatusChip } from '../StatusChip';
import { ReferredUserDetails as ReferredUserDetailsType } from '../types';

const MOCK_USER: ReferredUserDetailsType = {
  userId: 1001,
  displayName: 'LuckyPlay',
  emailAddress: 'luckyplay@gmail.com',
  status: 'Qualified',
  referredBy: 'user123',
  referredById: 501,
  registrationDate: '10-01-2024 10:24 AM',
  lastActive: '10-25-2024 08:15 PM',
  hasPurchased: true,
  rewardStatus: 'Claimed',
  silverCoins: 1000000,
  silverClaimDate: '10-03-2024 11:22 AM',
  goldCoins: 5,
  goldClaimDate: '10-03-2024 11:22 AM',
  totalPurchases: 3,
  totalAmount: 747,
  lastPurchaseDate: '10-20-2026 06:45 PM',
};

export default function ReferredUserDetails() {
  useParams();
  const navigate = useNavigate();
  const user = MOCK_USER;

  return (
    <Container maxWidth="xl">
      <Helmet><title>User Referral Details</title></Helmet>

      {/* Back */}
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: 'text.primary' }}>
          <Iconify icon="mdi:arrow-left" width={20} />
        </IconButton>
        <Typography variant="body1" fontWeight={500}>Go Back</Typography>
      </Stack>

      {/* User Info Card */}
      <Card sx={{ p: 3, borderRadius: '16px', mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Grid container spacing={2} alignItems="center">
          {/* Left: Avatar + basic info */}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
                backgroundColor: '#EEF1FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Iconify icon="mdi:account-plus-outline" width={30} sx={{ color: '#5B7FFF' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">User ID</Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5} pr={2}>
                  <Typography variant="h5" fontWeight={700}>{user.userId}</Typography>
                  <ReferralStatusChip status={user.status} />
                </Stack>
                <Typography variant="caption" color="text.secondary">Display Name</Typography>
                <Typography variant="body2" fontWeight={600} mb={0.5}>{user.displayName}</Typography>
                <Typography variant="caption" color="text.secondary">Email Address</Typography>
                <Typography variant="body2">{user.emailAddress}</Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Middle: Referred By + dates */}
          <Grid item xs={12} md={3.5} sx={{ borderLeft: { md: '1px solid #EEEEEE' }, pl: { md: 3 } }}>
            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Referred By</Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => navigate(`/dashboard/refer-a-friend/referrer/${user.referredById}`)}
                sx={{
                  mt: 0.5,
                  px: 1.5, py: 0.8,
                  backgroundColor: '#F4F6FF',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#E8ECFF' },
                  maxWidth: 240,
                }}
              >
                <Typography variant="body2" fontWeight={600} >
                  {user.referredBy} (ID: {user.referredById})
                </Typography>
                <Iconify icon="mdi:arrow-top-right" width={16} sx={{ color: '#3B5BDB', ml: 1, flexShrink: 0 }} />
              </Stack>
            </Box>
            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Registration Date</Typography>
              <Typography variant="body2" fontWeight={500}>{user.registrationDate}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Last Active</Typography>
              <Typography variant="body2" fontWeight={500}>{user.lastActive}</Typography>
            </Box>
          </Grid>

          {/* Right: Made Purchase + Reward Status */}
          <Grid item xs={12} md={4.4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', borderLeft: { md: '1px solid #EEEEEE' }, pl: { md: 3 } }}>
            <Stack spacing={1.5}>
              <Box sx={{
                backgroundColor: '#F5F0FF', borderRadius: '12px', p: 2,
                display: 'flex', alignItems: 'center', gap: 2,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%', backgroundColor: '#EDE0FF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Iconify icon="mdi:cart-outline" width={22} sx={{ color: '#7B2FBE' }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Made Purchase?</Typography>
                  <Typography variant="body1" fontWeight={700}>{user.hasPurchased ? 'Yes' : 'No'}</Typography>
                </Box>
              </Box>
              <Box sx={{
                backgroundColor: '#E8FBF3', borderRadius: '12px', p: 2,
                display: 'flex', alignItems: 'center', gap: 2,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%', backgroundColor: '#C8F5E1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Iconify icon="mdi:gift-outline" width={22} sx={{ color: '#1B7A4A' }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Reward Status</Typography>
                  <Typography variant="body1" fontWeight={700}>{user.rewardStatus}</Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Bottom Row: Referral Rewards + Purchase Summary */}
      <Grid container spacing={3}>
        {/* Referral Rewards */}
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3, borderRadius: '16px', height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>Referral Rewards</Typography>
            <Grid container spacing={2}>
              {/* Silver Coins */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ backgroundColor: '#EDF6FF', borderRadius: '14px', p: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%', backgroundColor: '#D0DCFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography fontWeight={800} fontSize="0.85rem" sx={{ color: '#3B5BDB' }}>S</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">Silver Coins</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" fontWeight={700}>
                      {user.silverCoins.toLocaleString()}
                    </Typography>
                    <RewardStatusChip status={user.rewardStatus} />
                  </Stack>
                  <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                    Claimed on: {user.silverClaimDate}
                  </Typography>
                </Box>
              </Grid>

              {/* Gold Coins */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ backgroundColor: '#EDF6FF', borderRadius: '14px', p: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%', backgroundColor: '#FFE8A0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography fontWeight={800} fontSize="0.85rem" sx={{ color: '#B8860B' }}>G</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">Gold Coins</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" fontWeight={700}>
                      {user.goldCoins.toLocaleString()}
                    </Typography>
                    <RewardStatusChip status={user.goldCoins > 0 ? 'Claimed' : 'Not Claimed'} />
                  </Stack>
                  <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                    Claimed on: {user.goldClaimDate}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Purchase Summary */}
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 3, borderRadius: '16px', height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#080A11' }} mb={2}>
              Purchase Summary
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2.5}>
              {/* Cart icon in rounded square */}
              <Box sx={{
                width: 64, height: 64, borderRadius: '16px', backgroundColor: '#F8EFFF',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Iconify icon="mdi:cart-outline" width={32} sx={{ color: '#A44CE8' }} />
              </Box>

              {/* Divider */}
              <Box sx={{ width: '1px', height: 56, backgroundColor: '#E4E8EF', flexShrink: 0 }} />

              {/* Total Purchases */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Total Purchases</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#080A11' }}>{user.totalPurchases}</Typography>
              </Box>

              {/* Divider */}
              <Box sx={{ width: '1px', height: 56, backgroundColor: '#E4E8EF', flexShrink: 0 }} />

              {/* Total Amount */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Total Amount</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#080A11' }}>{user.totalAmount} PHP</Typography>
              </Box>

              {/* Divider */}
              <Box sx={{ width: '1px', height: 56, backgroundColor: '#E4E8EF', flexShrink: 0 }} />

              {/* Last Purchase */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#607FAE' }} display="block">Last Purchase</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ color: '#080A11' }}>
                  {user.lastPurchaseDate.split(' ')[0]}
                </Typography>
                <Typography variant="caption" sx={{ color: '#607FAE' }}>
                  {user.lastPurchaseDate.split(' ').slice(1).join(' ')}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
